import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import Tooltip from '../../components/molecules/Tooltip';
import { Alert, Checkbox, Label, SwitchField } from '../../components/widgets';
import { SECTION_ACCESS_TYPES } from '../../utils/consts';
import AddFutureItems from '../containers/AddFutureItems';
import { isScreenViewOnly, VIEW_ONLY_PAGES_MAP } from '../sections';
import { SectionPermissionSchema } from '../types';
import { distributeIntoColumns, MEMBER_TYPES, translationPrefix } from '../utils';
import AccessTypeIcon from './AccessTypeIcon';
import ExpandableGroup from './ExpandableGroup';
import css from './ScreenPageStep.css';
import StepCard from './StepCard';

const COLUMNS_COUNT = 2;

const ScreenPageStep = ({
    screens,
    selectedScreens,
    toggleScreens,
    activeMemberType,
    sectionPermissions,
    updateSectionPermissions,
    translate,
    onStepValidationStatusChanged = () => {},
}) => {
    useEffect(() => {
        onStepValidationStatusChanged({ isValid: true, title: '' });
    }, [onStepValidationStatusChanged]);

    const toggleViewOnlyScreen = (screenName, newState) => {
        const sections = VIEW_ONLY_PAGES_MAP[screenName];
        if (!sections) return;
        const accessType = newState ? SECTION_ACCESS_TYPES.VIEW : SECTION_ACCESS_TYPES.EDIT;
        updateSectionPermissions(sections, accessType);
    };

    const toggleScreen = (screenName, newState) => {
        toggleScreens([screenName], !!newState);
        if (!newState && isScreenViewOnly(screenName, sectionPermissions)) {
            toggleViewOnlyScreen(screenName, false);
        }
    };

    const toggleItem = (item, newState) => {
        toggleScreens(
            item.screens.filter(screen => !screen.disabled).map(screen => screen.name),
            !!newState
        );
        if (!newState) {
            item.screens.forEach(screen => {
                if (isScreenViewOnly(screen.name, sectionPermissions)) {
                    toggleViewOnlyScreen(screen.name, false);
                }
            });
        }
    };

    const showViewOnlyToggle = screenName => {
        return Object.keys(VIEW_ONLY_PAGES_MAP).includes(screenName);
    };

    const renderScreen = screen => (
        <div key={screen.name} className={css.screenContainer}>
            {screen.warning && (
                <Icon
                    name="info-triangle"
                    size={IconSizes.X_SMALL}
                    variant={IconVariants.WARNING}
                    className={css.screenWarning}
                    interactive={false}
                />
            )}
            <Tooltip title={screen.disabledTooltip} disabled={!screen.disabledTooltip}>
                <Checkbox
                    size="small"
                    label={screen.displayName}
                    checked={selectedScreens.includes(screen.name)}
                    disabled={screen.disabled}
                    onChange={newState => toggleScreen(screen.name, newState)}
                />
            </Tooltip>
            {showViewOnlyToggle(screen.name) && (
                <Tooltip
                    titleTranslationKey="STATIC.PAGES.TEAM_MEMBERS.MANAGE_PERMISSIONS_PAGE.VIEW_ONLY_TOOLTIP"
                    wrapperClass={css.tooltipWrapper}
                >
                    <SwitchField
                        checked={isScreenViewOnly(screen.name, sectionPermissions)}
                        onChange={newState => {
                            toggleViewOnlyScreen(screen.name, newState);
                            if (newState) toggleScreen(screen.name, true);
                        }}
                        type="small"
                    />
                    <AccessTypeIcon
                        accessType={SECTION_ACCESS_TYPES.VIEW}
                        size={IconSizes.X_SMALL}
                        interactive={false}
                        className={css.viewOnlyIcon}
                        withTooltip={false}
                    />
                    <Label
                        type="p"
                        className={css.viewOnlyLabel}
                        text={`STATIC.PAGES.TEAM_MEMBERS.MANAGE_PERMISSIONS_PAGE.SECTION_ACCESS_TYPES_TITLE.${SECTION_ACCESS_TYPES.VIEW}`}
                    />
                </Tooltip>
            )}
        </div>
    );

    const renderColumn = columnItems => (
        <div className={css.column}>
            {columnItems.map(item => {
                const enabledChildren = item.screens.filter(child => !child.disabled);
                const selectedChildren = enabledChildren.filter(child => child.selected);
                const sectionChecked = selectedChildren.length === enabledChildren.length;
                const sectionIndeterminate =
                    selectedChildren.length > 0 && selectedChildren.length < enabledChildren.length;
                const hasWarnings = selectedChildren.filter(child => child.warning).length > 0;

                return (
                    <div key={item.title} className={css.item}>
                        <ExpandableGroup
                            title={<span className={css.title}>{item.title}</span>}
                            marked={sectionIndeterminate || sectionChecked}
                            hasWarning={hasWarnings}
                            headerAreaClassName={css.headerArea}
                            animatedAreaClassName={css.animatedArea}
                            rightComponents={
                                <Checkbox
                                    label={translate('STATIC.PAGES.TEAM_MEMBERS.SELECTED_COUNT', {
                                        selectedCount: selectedChildren.length,
                                        totalCount: item.screens.length,
                                    })}
                                    labelPosition="left"
                                    checked={sectionChecked}
                                    threeStatesEnabled
                                    indeterminate={sectionIndeterminate}
                                    onChange={newState => toggleItem(item, newState)}
                                />
                            }
                        >
                            {item.screens.map(renderScreen)}
                        </ExpandableGroup>
                    </div>
                );
            })}
        </div>
    );

    const items = screens.map(section => ({
        title: section.displayName,
        screens: section.children,
    }));
    const columns = distributeIntoColumns(items, COLUMNS_COUNT, item => item.screens.length);
    const hasWarnings = screens.some(section => section.children.some(screen => screen.warning));

    return (
        <div className={css.container}>
            {hasWarnings && (
                <Alert
                    className={css.alert}
                    alertIconClassName={css.alertIcon}
                    type="warning"
                    text={`${translationPrefix}.SCREEN_SELECTION_WARNING`}
                />
            )}
            <StepCard
                title={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.TITLE.SCREENS`}
                description={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.DESCRIPTION.${activeMemberType}.SCREENS`}
                className={css.stepCard}
                contentClassName={css.stepCardContent}
            >
                <div className={css.content}>{columns.map(renderColumn)}</div>
            </StepCard>
            {activeMemberType !== MEMBER_TYPES.AGENCY && (
                <AddFutureItems itemType="screens" form="screenSelectionForm" className={css.addFutureItems} />
            )}
        </div>
    );
};

ScreenPageStep.propTypes = {
    screens: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            displayName: PropTypes.string.isRequired,
            children: PropTypes.arrayOf(
                PropTypes.shape({
                    name: PropTypes.string.isRequired,
                    displayName: PropTypes.string.isRequired,
                    warning: PropTypes.bool.isRequired,
                    disabled: PropTypes.bool.isRequired,
                    disabledTooltip: PropTypes.string,
                })
            ).isRequired,
        })
    ).isRequired,
    sectionPermissions: PropTypes.arrayOf(SectionPermissionSchema).isRequired,
    updateSectionPermissions: PropTypes.func.isRequired,
    selectedScreens: PropTypes.arrayOf(PropTypes.string).isRequired,
    toggleScreens: PropTypes.func.isRequired,
    activeMemberType: PropTypes.string.isRequired,
    translate: PropTypes.func.isRequired,
    onStepValidationStatusChanged: PropTypes.func,
};

export default ScreenPageStep;
